/**
 * ШАПКА И ЛЕВОЕ МЕНЮ КАБИНЕТА СОБИРАЮТСЯ ОДИН РАЗ, А НЕ НА КАЖДОМ ПЕРЕХОДЕ.
 *
 * Владелец, 29.10.2026: «почему при переключени на варехауз левая панель кабинета пропадает на
 * короткое время?», «как то нездорово работает это всё», и про список: «райдеры тормозят появляться
 * теперь в кабинете… и все страницы кабинета внутри».
 *
 * ЗАМЕР в браузере, переход «мои райдеры → склад»: узел `#riders-page` удаляется, через 14 мс
 * добавляется `#my-equipment-page`, и `#side-nav` в нём — ДРУГОЙ элемент (сравнение по ссылке:
 * `false`). То есть каждая страница кабинета была маленькой перезагрузкой сайта: заново строилась
 * шапка, заново меню, заново тело, и только ПОСЛЕ этого страница начинала спрашивать сервер.
 * Строки появлялись через 1081, 1826 и 1635 мс от щелчка при самом запросе 172–680 мс.
 *
 * Поэтому оболочка живёт между переходами: меняется только `#page-body`, подсветка пункта меню
 * переставляется на месте (`components/side-nav.ts#setSideNavCurrent`), а `id` корня меняется,
 * потому что к нему привязаны стили отдельных страниц (`#riders-page`, `#admin-page` и прочие).
 *
 * ЧЕГО ЗДЕСЬ НЕТ. Это не «кеш страниц»: тело очищается, и страница рисует его заново, как и раньше.
 * Сэкономлены только шапка, меню и баннер про почту — то, что на всех страницах кабинета одинаково
 * и чего человек не просил перерисовывать.
 */
import { initAppHeader, renderAppHeaderHtml } from "../components/app-header";
import { initSideNav, renderSideNavHtml, setSideNavCurrent, type SideNavCurrent } from "../components/side-nav";
import { initVerifyEmailBanner, verifyEmailBannerHtml } from "../components/verify-email-banner";

export interface CabinetShellOptions {
  /** `id` корня страницы — к нему привязаны её собственные стили. */
  pageId: string;
  /** Какой пункт меню подсвечен. */
  current: SideNavCurrent;
  /** Класс тела страницы, если он у неё есть (`admin-page`, `profile-page`). */
  bodyClass?: string;
  /** Баннер про непроверенную почту. Есть не у всех страниц кабинета — у профиля и администрации
   *  его не было и до этой правки, и добавлять его молча значило бы менять вид без просьбы. */
  verifyEmailBanner?: boolean;
}

/** Ставит или снимает баннер про непроверенную почту у уже собранной оболочки. Разметка одна и та
 *  же, что при первой сборке; заводится он только когда появился, иначе `initVerifyEmailBanner`
 *  повесил бы вторые обработчики на те же кнопки. */
function syncVerifyEmailBanner(page: HTMLElement, wanted: boolean): void {
  const present = page.querySelector<HTMLElement>("#verify-email-banner");
  if (wanted && !present) {
    const layout = page.querySelector<HTMLElement>("#page-layout");
    if (!layout) return;
    layout.insertAdjacentHTML("beforebegin", verifyEmailBannerHtml());
    initVerifyEmailBanner(page);
    return;
  }
  if (!wanted && present) present.remove();
}

/** Отдаёт `#page-body`, готовое к заполнению: пустое, с нужным классом, прокрученное к началу. */
export function ensureCabinetShell(container: HTMLElement, options: CabinetShellOptions): HTMLElement {
  const existing = container.querySelector<HTMLElement>(".cabinet-page");
  const body = existing?.querySelector<HTMLElement>("#page-body");
  // Меню ОБЯЗАТЕЛЬНО для повторного использования: у склада есть гостевой вид без левого меню
  // (`pages/my-equipment-page.ts`), и принять его за готовую оболочку значило бы оставить вошедшего
  // человека без меню.
  const reusable = existing !== null && body != null && existing.querySelector("#side-nav") !== null;

  if (existing && body && reusable) {
    existing.id = options.pageId;
    setSideNavCurrent(existing, options.current);
    // БАННЕР ПРО ПОЧТУ СТАВИТСЯ И СНИМАЕТСЯ ПО ОПЦИИ, А НЕ ПЕРЕЖИВАЕТ ПЕРЕХОД (находка Линнея при
    // проверке PR 605). Первая редакция оболочки на повторном использовании его не трогала, и выходило
    // наоборот сказанному: с райдеров баннер уезжал в профиль и администрацию, где его не было и до
    // этой правки, а из администрации в райдеры возвращался уже без него. Видят это только люди с
    // непроверенной почтой — то есть первые дни нового человека, где вид и важнее всего.
    syncVerifyEmailBanner(existing, options.verifyEmailBanner !== false);
    body.className = options.bodyClass ?? "";
    body.innerHTML = "";
    // Новый раздел читается с начала, а не с того места, где человек бросил предыдущий. На
    // компьютере едет тело, на телефоне — вся страница (`style.css`, порог 640), поэтому сбрасывать
    // надо оба: на телефоне `body.scrollTop` всегда ноль и один он ничего не делает (вторая находка
    // Линнея там же).
    body.scrollTop = 0;
    if (typeof window !== "undefined") window.scrollTo(0, 0);
    return body;
  }

  container.innerHTML = `
    <div id="${options.pageId}" class="cabinet-page">
      ${renderAppHeaderHtml()}
      ${options.verifyEmailBanner === false ? "" : verifyEmailBannerHtml()}
      <div id="page-layout">
        ${renderSideNavHtml(options.current)}
        <div id="page-body"${options.bodyClass ? ` class="${options.bodyClass}"` : ""}></div>
      </div>
    </div>`;
  initAppHeader(container);
  initSideNav(container);
  if (options.verifyEmailBanner !== false) initVerifyEmailBanner(container);
  const fresh = container.querySelector<HTMLElement>("#page-body");
  if (!fresh) throw new Error("cabinet-shell: #page-body не собрался");
  return fresh;
}

/** Есть ли в контейнере живая оболочка кабинета — спрашивает `app-shell`, решая, стирать ли его
 *  содержимое перед монтированием следующей страницы. */
export function hasCabinetShell(container: HTMLElement): boolean {
  return container.querySelector(".cabinet-page #page-layout #side-nav") !== null;
}
